import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { loadInstalledExtensions, resetTheme } from '../../services/extensionRuntime'
import { useAppStore } from '../../store/appStore'

interface Extension {
  id: string; name: string; slug: string; description: string; type: string
  author_name: string; version: string; downloads: number; rating: number
  tags: string; manifest?: string; installed_at?: string
}

const TYPE_ICONS: Record<string, string> = { theme: '🎨', snippets: '✂️', 'ai-prompts': '🤖', 'language': '📝' }
const TYPE_LABELS: Record<string, string> = { theme: 'Theme', snippets: 'Snippets', 'ai-prompts': 'AI Prompts', language: 'Language' }

export function MarketplacePanel() {
  const [tab, setTab] = useState<'browse' | 'installed' | 'integrations' | 'publish'>('browse')
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [installed, setInstalled] = useState<Extension[]>([])
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [sort, setSort] = useState('popular')
  const [loading, setLoading] = useState(false)

  const loadExtensions = useCallback(async () => {
    setLoading(true)
    const params = new URLSearchParams()
    if (search) params.set('q', search)
    if (typeFilter !== 'all') params.set('type', typeFilter)
    params.set('sort', sort)
    const res = await authFetch(apiUrl(`/api/marketplace?${params}`))
    const data = await res.json()
    setExtensions(Array.isArray(data) ? data : [])
    setLoading(false)
  }, [search, typeFilter, sort])

  const loadInstalled = useCallback(async () => {
    const res = await authFetch(apiUrl('/api/marketplace/installed'))
    const data = await res.json()
    setInstalled(Array.isArray(data) ? data : [])
  }, [])

  useEffect(() => { loadExtensions(); loadInstalled() }, [loadExtensions, loadInstalled])

  const install = async (id: string) => {
    await authFetch(apiUrl('/api/marketplace/install'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ extensionId: id }) })
    loadInstalled()
    loadExtensions()
    loadInstalledExtensions() // Reload runtime (themes, snippets, prompts)
  }

  const uninstall = async (id: string) => {
    await authFetch(apiUrl('/api/marketplace/uninstall'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ extensionId: id }) })
    loadInstalled()
    resetTheme() // Reset theme in case it was a theme extension
    loadInstalledExtensions() // Reload runtime
  }

  const isInstalled = (id: string) => installed.some(e => e.id === id)

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span>MARKETPLACE</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['browse', 'installed', 'integrations', 'publish'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700,
              border: tab === t ? '1px solid var(--accent)' : '1px solid var(--border-2)',
              background: tab === t ? 'rgba(124,110,245,0.1)' : 'none',
              color: tab === t ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {tab === 'browse' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Search & filters */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search extensions..." style={inputStyle} />
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              {['all', 'theme', 'snippets', 'ai-prompts'].map(t => (
                <button key={t} onClick={() => setTypeFilter(t)} style={{
                  padding: '2px 7px', borderRadius: 10, fontSize: 9, fontFamily: 'var(--font-mono)',
                  border: typeFilter === t ? '1px solid var(--accent)' : '1px solid var(--border-2)',
                  background: typeFilter === t ? 'rgba(124,110,245,0.08)' : 'none',
                  color: typeFilter === t ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
                }}>{t === 'all' ? 'All' : TYPE_LABELS[t] ?? t}</button>
              ))}
              <div style={{ flex: 1 }} />
              <select value={sort} onChange={e => setSort(e.target.value)} style={{ ...inputStyle, flex: 'none', width: 80, padding: '2px 4px', fontSize: 9 }}>
                <option value="popular">Popular</option>
                <option value="rating">Top Rated</option>
                <option value="newest">Newest</option>
              </select>
            </div>
          </div>

          {/* Extension list */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {loading && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11 }}>Loading...</div>}
            {!loading && extensions.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11 }}>No extensions found</div>}
            {extensions.map(ext => (
              <ExtensionCard key={ext.id} ext={ext} installed={isInstalled(ext.id)} onInstall={() => install(ext.id)} onUninstall={() => uninstall(ext.id)} />
            ))}
          </div>
        </div>
      )}

      {tab === 'installed' && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          {installed.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>No extensions installed</div>
          ) : installed.map(ext => (
            <ExtensionCard key={ext.id} ext={ext} installed={true} onInstall={() => {}} onUninstall={() => uninstall(ext.id)} />
          ))}
        </div>
      )}

      {tab === 'integrations' && <IntegrationsTab />}

      {tab === 'publish' && <PublishForm onPublished={() => { setTab('browse'); loadExtensions() }} />}
    </div>
  )
}

function ExtensionCard({ ext, installed, onInstall, onUninstall }: { ext: Extension; installed: boolean; onInstall: () => void; onUninstall: () => void }) {
  let tags: string[] = []
  try { tags = JSON.parse(ext.tags ?? '[]') } catch {}

  return (
    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>{TYPE_ICONS[ext.type] ?? '📦'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-0)' }}>{ext.name}</span>
          <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>v{ext.version}</span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', marginTop: 2, lineHeight: 1.4 }}>{ext.description}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{ext.author_name}</span>
          <span style={{ fontSize: 9, color: 'var(--text-2)' }}>·</span>
          <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>⬇ {formatNum(ext.downloads)}</span>
          {ext.rating > 0 && <><span style={{ fontSize: 9, color: 'var(--text-2)' }}>·</span><span style={{ fontSize: 9, color: 'var(--accent-3)' }}>★ {ext.rating.toFixed(1)}</span></>}
        </div>
        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>
            {tags.map(t => <span key={t} style={{ fontSize: 8, padding: '1px 5px', borderRadius: 8, background: 'var(--bg-3)', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{t}</span>)}
          </div>
        )}
      </div>
      <button onClick={installed ? onUninstall : onInstall} style={{
        padding: '4px 10px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
        border: installed ? '1px solid var(--red)' : '1px solid var(--accent-2)',
        background: installed ? 'rgba(240,112,112,0.08)' : 'rgba(95,212,164,0.08)',
        color: installed ? 'var(--red)' : 'var(--accent-2)',
      }}>{installed ? 'Remove' : 'Install'}</button>
    </div>
  )
}

function PublishForm({ onPublished }: { onPublished: () => void }) {
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [type, setType] = useState('snippets')
  const [manifest, setManifest] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState('')

  const publish = async () => {
    if (!name.trim()) { setError('Name required'); return }
    setPublishing(true); setError('')
    try {
      let parsedManifest = {}
      if (manifest.trim()) parsedManifest = JSON.parse(manifest)
      const res = await authFetch(apiUrl('/api/marketplace/publish'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description: desc, type, tags: [type], manifest: parsedManifest }),
      })
      if (!res.ok) { const d = await res.json(); setError(d.error ?? 'Failed'); return }
      onPublished()
    } catch (e: any) { setError(e.message ?? 'Invalid JSON') }
    finally { setPublishing(false) }
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
      <div style={fieldStyle}><label style={labelStyle}>Name</label><input value={name} onChange={e => setName(e.target.value)} placeholder="My Extension" style={inputStyle} /></div>
      <div style={fieldStyle}><label style={labelStyle}>Description</label><input value={desc} onChange={e => setDesc(e.target.value)} placeholder="What does it do?" style={inputStyle} /></div>
      <div style={fieldStyle}><label style={labelStyle}>Type</label>
        <select value={type} onChange={e => setType(e.target.value)} style={inputStyle}>
          <option value="theme">Theme</option>
          <option value="snippets">Snippets</option>
          <option value="ai-prompts">AI Prompts</option>
        </select>
      </div>
      <div style={fieldStyle}><label style={labelStyle}>Manifest (JSON)</label>
        <textarea value={manifest} onChange={e => setManifest(e.target.value)} placeholder='{"snippets": [...]}' style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />
      </div>
      {error && <div style={{ color: 'var(--red)', fontSize: 10, marginBottom: 8, fontFamily: 'var(--font-mono)' }}>{error}</div>}
      <button onClick={publish} disabled={publishing} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>
        {publishing ? 'Publishing...' : 'Publish Extension'}
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════
// c08 — API Integrations Marketplace
// ═══════════════════════════════════════════

interface Integration {
  id: string
  name: string
  icon: string
  category: string
  description: string
  envVars: string[]
  package: string
}

const API_INTEGRATIONS: Integration[] = [
  { id: 'stripe', name: 'Stripe', icon: '💳', category: 'Payments', description: 'Accept payments, subscriptions, and invoices', envVars: ['STRIPE_SECRET_KEY', 'STRIPE_PUBLISHABLE_KEY'], package: 'stripe' },
  { id: 'auth0', name: 'Auth0', icon: '🔐', category: 'Auth', description: 'Authentication & authorization with social logins', envVars: ['AUTH0_DOMAIN', 'AUTH0_CLIENT_ID', 'AUTH0_CLIENT_SECRET'], package: '@auth0/nextjs-auth0' },
  { id: 'supabase', name: 'Supabase', icon: '⚡', category: 'Database', description: 'PostgreSQL database, auth, storage, and realtime', envVars: ['SUPABASE_URL', 'SUPABASE_ANON_KEY'], package: '@supabase/supabase-js' },
  { id: 'sendgrid', name: 'SendGrid', icon: '📧', category: 'Email', description: 'Transactional and marketing email delivery', envVars: ['SENDGRID_API_KEY'], package: '@sendgrid/mail' },
  { id: 'twilio', name: 'Twilio', icon: '📱', category: 'SMS', description: 'SMS, voice calls, and WhatsApp messaging', envVars: ['TWILIO_ACCOUNT_SID', 'TWILIO_AUTH_TOKEN', 'TWILIO_PHONE_NUMBER'], package: 'twilio' },
  { id: 'openai', name: 'OpenAI', icon: '🤖', category: 'AI', description: 'GPT models, embeddings, DALL-E, and Whisper', envVars: ['OPENAI_API_KEY'], package: 'openai' },
  { id: 'google-maps', name: 'Google Maps', icon: '🗺️', category: 'Maps', description: 'Maps, geocoding, directions, and places', envVars: ['GOOGLE_MAPS_API_KEY'], package: '@googlemaps/js-api-loader' },
  { id: 'cloudinary', name: 'Cloudinary', icon: '🖼️', category: 'Media', description: 'Image and video upload, transformation, and CDN', envVars: ['CLOUDINARY_CLOUD_NAME', 'CLOUDINARY_API_KEY', 'CLOUDINARY_API_SECRET'], package: 'cloudinary' },
  { id: 'firebase', name: 'Firebase', icon: '🔥', category: 'Backend', description: 'Auth, Firestore, storage, hosting, and functions', envVars: ['FIREBASE_API_KEY', 'FIREBASE_PROJECT_ID'], package: 'firebase' },
  { id: 'resend', name: 'Resend', icon: '✉️', category: 'Email', description: 'Modern email API for developers', envVars: ['RESEND_API_KEY'], package: 'resend' },
  { id: 'aws-s3', name: 'AWS S3', icon: '☁️', category: 'Storage', description: 'Object storage for files, images, and backups', envVars: ['AWS_ACCESS_KEY_ID', 'AWS_SECRET_ACCESS_KEY', 'AWS_S3_BUCKET'], package: '@aws-sdk/client-s3' },
  { id: 'redis', name: 'Redis', icon: '🔴', category: 'Cache', description: 'In-memory cache, sessions, and pub/sub', envVars: ['REDIS_URL'], package: 'ioredis' },
]

const INTEGRATION_CATEGORIES = [...new Set(API_INTEGRATIONS.map(i => i.category))]

function IntegrationsTab() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [connected, setConnected] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('pulse-integrations') ?? '[]')) } catch { return new Set() }
  })
  const [connecting, setConnecting] = useState<string | null>(null)
  const { openTabs, activeTabId, markDirty } = useAppStore()

  const filtered = API_INTEGRATIONS.filter(i => {
    if (category && i.category !== category) return false
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q) || i.category.toLowerCase().includes(q)
  })

  const connectIntegration = async (integration: Integration) => {
    setConnecting(integration.id)
    try {
      // Ask AI to generate wiring code
      const res = await authFetch(apiUrl('/api/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Generate a minimal setup file for ${integration.name} integration. Include: 1) import of "${integration.package}", 2) initialization with env vars (${integration.envVars.join(', ')}), 3) one example helper function. Output ONLY the code, no explanation. Use TypeScript.` }],
          context: { model: 'gemini-2.5-flash' },
        }),
      })

      let code = ''
      if (res.ok) {
        const reader = res.body?.getReader()
        const decoder = new TextDecoder()
        if (reader) {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            const chunk = decoder.decode(value)
            for (const line of chunk.split('\n')) {
              if (line.startsWith('data: ')) {
                try {
                  const parsed = JSON.parse(line.slice(6))
                  if (parsed.text) code += parsed.text
                  if (parsed.done) break
                } catch {}
              }
            }
          }
        }
      }

      // Fallback if AI didn't return code
      if (!code.trim()) {
        code = `// ${integration.name} Integration\nimport ${integration.id.replace(/-/g, '')} from '${integration.package}'\n\n// Required env vars: ${integration.envVars.join(', ')}\n// Add these to your .env file\n\nexport const ${integration.id.replace(/-/g, '')}Client = /* initialize here */\n`
      }

      // Strip markdown fences if present
      code = code.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '').trim()

      // Insert into active file, or create a new file
      const tab = openTabs.find(t => t.id === activeTabId)
      if (tab) {
        markDirty(tab.id, tab.content + '\n\n' + code + '\n')
      } else {
        // Create a new integration file on the server
        const filePath = `lib/integrations/${integration.id}.ts`
        try {
          await authFetch(apiUrl('/api/files/write'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: filePath, content: code }),
          })
          // Open the newly created file in the editor
          const { openTab } = useAppStore.getState()
          openTab({ id: filePath, path: '/' + filePath, name: `${integration.id}.ts`, language: 'typescript', content: code })
          window.dispatchEvent(new CustomEvent('pulse:files-changed'))
        } catch {}
      }

      // Mark as connected
      const next = new Set(connected)
      next.add(integration.id)
      setConnected(next)
      localStorage.setItem('pulse-integrations', JSON.stringify([...next]))
    } catch {} finally {
      setConnecting(null)
    }
  }

  const disconnect = (id: string) => {
    const next = new Set(connected)
    next.delete(id)
    setConnected(next)
    localStorage.setItem('pulse-integrations', JSON.stringify([...next]))
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search integrations..." style={inputStyle} />
        <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setCategory(null)} style={{ padding: '2px 7px', borderRadius: 10, fontSize: 9, fontFamily: 'var(--font-mono)', border: !category ? '1px solid var(--accent)' : '1px solid var(--border-2)', background: !category ? 'rgba(124,110,245,0.08)' : 'none', color: !category ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer' }}>All</button>
          {INTEGRATION_CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(category === c ? null : c)} style={{ padding: '2px 7px', borderRadius: 10, fontSize: 9, fontFamily: 'var(--font-mono)', border: category === c ? '1px solid var(--accent)' : '1px solid var(--border-2)', background: category === c ? 'rgba(124,110,245,0.08)' : 'none', color: category === c ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer' }}>{c}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {filtered.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>No integrations found</div>}
        {filtered.map(integ => {
          const isConnected = connected.has(integ.id)
          const isLoading = connecting === integ.id
          return (
            <div key={integ.id} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>{integ.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-0)' }}>{integ.name}</span>
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 8, background: 'var(--bg-3)', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{integ.category}</span>
                  {isConnected && <span style={{ fontSize: 9, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>✓ Connected</span>}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{integ.description}</div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>📦 {integ.package} · 🔑 {integ.envVars.length} env var{integ.envVars.length > 1 ? 's' : ''}</div>
              </div>
              <button onClick={() => isConnected ? disconnect(integ.id) : connectIntegration(integ)} disabled={isLoading} style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                border: isConnected ? '1px solid var(--red)' : '1px solid var(--accent-2)',
                background: isConnected ? 'rgba(240,112,112,0.08)' : 'rgba(95,212,164,0.08)',
                color: isConnected ? 'var(--red)' : 'var(--accent-2)',
                opacity: isLoading ? 0.5 : 1,
              }}>{isLoading ? '...' : isConnected ? 'Remove' : 'Connect'}</button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatNum(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
  return String(n)
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const inputStyle: React.CSSProperties = { width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const fieldStyle: React.CSSProperties = { marginBottom: 10 }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }
